import { IonContent, IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { OnboardingQuiz } from './OnboardingQuiz';
import { useUpdateProfileOnboarding } from '../../hooks/useProfile';
import { useProfileStore } from '../../store/useProfileStore';
import { notify } from '../../services/notify';
import { fireConfetti } from '../../utils/confetti';

interface ChefApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Optional override for the intro copy above the quiz. Defaults to the
   * generic "Complete the chef food safety quiz…" body. The publish-flow gate
   * passes its own "One step away!…" copy.
   */
  bodyText?: string;
  /**
   * Called after the user successfully becomes a chef (the onboarding patch
   * resolved), before `onClose`. Used by the publish flow to resume the
   * publish once the eater has converted.
   */
  onCompleted?: () => void;
}

export const ChefApplicationModal: React.FC<ChefApplicationModalProps> = ({
  isOpen,
  onClose,
  bodyText,
  onCompleted,
}) => {
  const { t } = useTranslation('onboarding');
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const updateOnboarding = useUpdateProfileOnboarding();

  const handleComplete = async (answers: Record<number, string[]>) => {
    if (!activeProfile) return;

    const now = new Date().toISOString();
    const existing = activeProfile.metadata?.onboarding;

    try {
      await updateOnboarding.mutateAsync({
        profileId: activeProfile.id,
        onboarding: {
          selectedRole: 'chef',
          quizAnswers: { ...existing?.quizAnswers, ...answers },
          quizCompletedAt: now,
          onboardingCompletedAt: existing?.onboardingCompletedAt ?? now,
        },
      });
      fireConfetti();
      notify.success(t('chefApplication.successToast'));
      onCompleted?.();
      onClose();
    } catch {
      notify.error(t('chefApplication.errorSave'));
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('chefApplication.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>{t('common:actions.close')}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="mx-auto w-full max-w-md px-6 py-8">
          <p className="mb-6 text-center text-sm text-muted-foreground">
            {bodyText ?? t('chefApplication.body')}
          </p>
          <OnboardingQuiz role="chef" onComplete={handleComplete} />
        </div>
      </IonContent>
    </IonModal>
  );
};
